import {Box, Button, Grid, IconButton, Stack, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import {WalkupMusicSelectionTable} from '../WalkupMusicSelectionTable';
import {
  AllowedPlayerTypeKeys,
  ListPlayerTypeBased,
} from '../stepperComponents/SongMappingStepperCmpt';
import {INewWalkupPlayer} from '../stepperComponents/WalkupLineupStepper';
import IconAdd from '../../../../asset/icons/users.svg';

const DIALOG_HEIGHTS = {
  mobile: {
    full: '100vh',
    content: 'calc(100vh - 160px)',
    playerList: '80vh',
  },
} as const;

export interface MobileViewProps {
  isVisible: boolean;
  playerId: number;
  setPlayerId: React.Dispatch<React.SetStateAction<number>>;
  selectedPlayerType: AllowedPlayerTypeKeys;
  setSelectedPlayerType: React.Dispatch<
    React.SetStateAction<AllowedPlayerTypeKeys>
  >;
  currentFormStudents: INewWalkupPlayer[];
  selectedPlayer?: INewWalkupPlayer;
  track: IMediaTrack[];
  onSelect: (track: IMediaTrack) => void;
  onDeSelect: (track: IMediaTrack) => void;
  handleNextPlayer: () => void;
  isLastPlayer: boolean;
  hasAllPlayersSelected: boolean;
  submit: () => void;
  isAthlete: boolean;
  songSelectionRef: React.RefObject<HTMLDivElement>;
}

export const WalkupMobileView = React.memo<MobileViewProps>(
  ({
    isVisible,
    playerId,
    setPlayerId,
    selectedPlayerType,
    setSelectedPlayerType,
    currentFormStudents,
    selectedPlayer,
    track,
    onSelect,
    onDeSelect,
    handleNextPlayer,
    isLastPlayer,
    hasAllPlayersSelected,
    submit,
    isAthlete,
    songSelectionRef,
  }) => {
    if (!isVisible) return null;

    return (
      <Box
        sx={{height: '100vh', display: {xs: 'block', sm: 'block', md: 'none'}}}>
        <Box textAlign='center' sx={{pt: 2, pb: 2, mb: 1}}>
          <Typography
            className='text-center font-bold'
            variant='h4'
            sx={{lineHeight: '25px !important'}}>
            STUDENT SELECTION FORM
          </Typography>
        </Box>
        <Box
          sx={{
            background: 'var(--blue-variant6)',
            paddingX: 1,
            py: 1,
            maxWidth: 'xl',
            mx: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
          className='text-center'>
          <Box
            sx={{
              flex: 1,
              flexDirection: 'row',
              minHeight: 0,
              overflow: 'hidden',
            }}>
            <Grid container sx={{height: '80vh'}}>
              <Box className='pt-2 pb-2 px-8 h-50'>
                <Typography variant='h6' className='font-bold mr-8'>
                  Select a player to assign song
                </Typography>
              </Box>
              {!playerId && (
                <Grid
                  item
                  xs={12}
                  className='playerList'
                  sx={{
                    background: `var(--blue_variant36)`,
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  <Box
                    className='flex-column overflow-auto scrollbar'
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: 'visible',
                    }}>
                    {isAthlete ? (
                      <ListPlayerTypeBased
                        selectedPlayerType={selectedPlayerType}
                        setSelectedPlayerType={setSelectedPlayerType}
                        playerId={playerId}
                        setPlayerId={setPlayerId}
                        players={currentFormStudents}
                        playerType='athlete'
                        isStudentForm={true}
                      />
                    ) : (
                      <Box
                        sx={{display: 'flex', flexDirection: 'column', gap: 0}}>
                        <Box
                          sx={{flex: '1', overflow: 'visible'}}
                          className='overflow-auto scrollbar'>
                          <ListPlayerTypeBased
                            selectedPlayerType={selectedPlayerType}
                            setSelectedPlayerType={setSelectedPlayerType}
                            playerId={playerId}
                            setPlayerId={setPlayerId}
                            players={currentFormStudents}
                            playerType='hitter'
                            isStudentForm={true}
                          />
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: '5px',
                            background: 'var(--blue-variant6)',
                            my: 1,
                          }}></Box>
                        <Box
                          sx={{flex: '1', overflow: 'visible'}}
                          className='overflow-auto scrollbar'>
                          <ListPlayerTypeBased
                            selectedPlayerType={selectedPlayerType}
                            setSelectedPlayerType={setSelectedPlayerType}
                            playerId={playerId}
                            setPlayerId={setPlayerId}
                            players={currentFormStudents}
                            playerType='pitcher'
                            isStudentForm={true}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
              {playerId !== 0 && (
                <Grid
                  item
                  xs={12}
                  className='walkupTrackSelection calcheight-52 scrollbar overflow-auto'
                  sx={{overflow: 'visible'}}>
                  <Box
                    ref={songSelectionRef}
                    sx={{
                      position: 'fixed',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '90%',
                      maxHeight: DIALOG_HEIGHTS.mobile.content,
                      overflow: 'visible',
                      background: 'var(--blue-variant6)',
                      paddingX: 1,
                      py: 1,
                      borderRadius: '10px',
                      zIndex: 1300,
                      boxShadow: 24,
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <img
                          src={IconAdd}
                          alt='No data'
                          className='noDataImg mr-4'
                          style={{width: '30px', height: '30px'}}
                        />
                        <Typography variant='h6' sx={{fontWeight: 'bold'}}>
                          {selectedPlayer
                            ? `#${selectedPlayer.player_no} - ${selectedPlayer.first_name} ${selectedPlayer.last_name}`
                            : 'Select a Player'}
                        </Typography>
                        <Typography
                          variant='h6'
                          className='text-center'
                          sx={{fontWeight: 'bold'}}>
                          {`(${
                            selectedPlayerType.charAt(0).toUpperCase() +
                            selectedPlayerType.slice(1)
                          })`}
                        </Typography>
                      </Box>
                      <IconButton
                        aria-label='Close player selection dialog'
                        onClick={() => setPlayerId(0)}
                        sx={{
                          color: 'white',
                          '&:hover': {bgcolor: 'rgba(255, 255, 255, 0.1)'},
                        }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <WalkupMusicSelectionTable
                      selectedTracks={track}
                      onSelect={onSelect}
                      onDeselect={onDeSelect}
                      isAddDisabled={playerId === 0}
                    />
                    <Stack
                      className='flex justify-center items-center mt-1'
                      direction='row'
                      spacing={2}
                      sx={{display: {xs: 'flex', md: 'none'}}}>
                      <Button variant='outlined' onClick={() => setPlayerId(0)}>
                        Back to Player List
                      </Button>
                      <Button
                        variant='contained'
                        onClick={handleNextPlayer}
                        disabled={
                          !selectedPlayer?.[selectedPlayerType]?.track &&
                          !selectedPlayer?.[selectedPlayerType]?.walkup_track_id
                        }>
                        {isLastPlayer ? 'Done' : 'Next Player'}
                      </Button>
                    </Stack>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          {!playerId && (
            <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
              <Stack
                className='flex justify-center items-center'
                direction='row'
                spacing={2}
                sx={{
                  p: 2,
                }}>
                <Button
                  variant='contained'
                  onClick={submit}
                  disabled={!hasAllPlayersSelected}>
                  Submit
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);

WalkupMobileView.displayName = 'WalkupMobileView';
